import { cn } from "lib/utils";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLpConfig } from "routes/LandingPage/LpConfigContext";
import { trackLpCta } from "routes/LandingPage/lp-analytics";

/** 右端の CTA ボタンの遷移先（両 LP 共通でフォームへ） */
const NAVBAR_CTA_HREF = "#cta";

/**
 * ランディングページ固定ナビゲーションバー
 * スクロールに応じて透明からホワイト背景に変化し、モバイルではハンバーガーメニューを表示する
 * @return {JSX.Element} ナビゲーションバーコンポーネント
 */
export function LpNavbar() {
  const lpConfig = useLpConfig();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent",
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo */}
          <a
            href={lpConfig.navbar.logoHref ?? "/"}
            className="flex items-center gap-2"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/strix_search_icon.png`}
              alt="StrixSearch"
              className="h-10"
            />
            <span
              className={cn(
                "text-xl tracking-[0.2em] font-extrabold transition-colors",
                scrolled ? "text-[#0F1649]" : "text-white",
              )}
              style={{
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                textShadow: scrolled ? "none" : "0 0 20px rgba(43,146,255,0.3)",
              }}
            >
              STRIX SEARCH
            </span>
          </a>

          {/* Center: Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {lpConfig.navbar.links.map(link => (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  "text-sm font-medium transition-colors",
                  scrolled
                    ? "text-gray-600 hover:text-[#0F1649]"
                    : "text-gray-300 hover:text-white",
                )}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right: CTA + Mobile Toggle */}
          <div className="flex items-center gap-4">
            {/* スマホ幅でも同じボタンが出る（モバイルメニューは項目だけ） */}
            <a
              href={NAVBAR_CTA_HREF}
              onClick={() => trackLpCta("navbar", NAVBAR_CTA_HREF)}
              className="bg-[#ff8d00] text-white rounded-lg px-5 py-2.5 font-semibold hover:opacity-90 transition-opacity text-sm"
            >
              {lpConfig.navbar.ctaLabel}
            </a>

            <button
              type="button"
              className={cn(
                "md:hidden p-2 rounded-lg transition-colors",
                scrolled
                  ? "text-[#0F1649] hover:bg-gray-100"
                  : "text-white hover:bg-white/10",
              )}
              onClick={() => setMobileOpen(prev => !prev)}
              aria-label="メニューを開閉"
            >
              {mobileOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "md:hidden overflow-hidden transition-all duration-300",
          mobileOpen ? "max-h-80" : "max-h-0",
          scrolled
            ? "bg-white/95 backdrop-blur-md"
            : "bg-[#02072F]/95 backdrop-blur-md",
        )}
      >
        <div className="px-4 py-4 space-y-2">
          {lpConfig.navbar.links.map(link => (
            <a
              key={link.href}
              href={link.href}
              className={cn(
                "block px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                scrolled
                  ? "text-gray-600 hover:text-[#0F1649] hover:bg-gray-50"
                  : "text-gray-300 hover:text-white hover:bg-white/10",
              )}
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}
